import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { Search, ChevronDown } from "lucide-react";

interface Option {
  label: string;
  value: string | number;
}

interface SelectedDoctor {
  name: string;
  mappingId: string | number;
}

interface SearchableDropdownProps {
  options?: Option[];
  onChange: (option: Option) => void;
  value?: string;
  placeholder?: string;
  selectedDoctor?: SelectedDoctor;
  doctorsData?: any[];
  className?: string;
  /** 🆕 message shown when no results are found */
  noOptionsMessage?: string;
  /** 🆕 placeholder for the search input */
  searchPlaceholder?: string;
  /** 🆕 disable search functionality */
  disableSearch?: boolean;
  /** 🆕 disable text truncation */
  disableTruncation?: boolean;
  /** Draw the open list over the page (portal, fixed position) so a scrolling
   *  parent such as a modal body cannot clip it; it flips above the field when
   *  there is no room below, and shows a visible scrollbar. Off by default. */
  floating?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  options = [],
  onChange,
  value,
  placeholder = "Select an option",
  selectedDoctor = { name: "", mappingId: "" },
  doctorsData = [],
  className,
  noOptionsMessage = "No options available",
  searchPlaceholder = "Search...",
  disableSearch = false,
  disableTruncation = false,
  floating = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredOptions, setFilteredOptions] = useState<Option[]>(options);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; width: number; top?: number; bottom?: number; maxHeight: number } | null>(null);

  // floating: place the list under (or above) the field, and keep it there while
  // anything scrolls or the window resizes.
  useLayoutEffect(() => {
    if (!floating || !isOpen) return;
    const place = () => {
      const r = dropdownRef.current?.getBoundingClientRect();
      if (!r) return;
      const below = window.innerHeight - r.bottom - 8;
      const above = r.top - 8;
      const openUp = below < 220 && above > below;
      const maxHeight = Math.max(160, Math.min(320, openUp ? above : below));
      setPos(
        openUp
          ? { left: r.left, width: r.width, bottom: window.innerHeight - r.top + 4, maxHeight }
          : { left: r.left, width: r.width, top: r.bottom + 4, maxHeight }
      );
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [floating, isOpen]);

  useEffect(() => {
    if (disableSearch || !searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options, disableSearch]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !(listRef.current && listRef.current.contains(event.target as Node))
      ) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen && searchInputRef.current && !disableSearch) {
      searchInputRef.current.focus();
    }
  }, [isOpen, disableSearch]);

  const handleOptionSelect = (option: Option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm("");
  };

  const getDisplayValue = () => {
    if (options?.length === 0) {
      return noOptionsMessage;
    }

    if (!disableTruncation && selectedDoctor.name.length > 8) {
      return `${selectedDoctor.name.substring(0, 8)}...`;
    }

    return selectedDoctor.name || placeholder;
  };

  const panel = (
    <div className={`${floating ? "" : "absolute z-50 mt-1 "}w-full rounded-lg border border-gray-200 bg-white shadow-lg overflow-hidden`}>
      {!disableSearch && (
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={searchPlaceholder} // ✅ dynamic
              className="w-full rounded-md border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm placeholder-gray-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 h-8"
            />
          </div>
        </div>
      )}

      <div
        className={floating ? "overflow-y-scroll" : "max-h-[150px] overflow-y-auto side_bar"}
        style={floating && pos ? { maxHeight: pos.maxHeight - 56, scrollbarWidth: "thin", scrollbarColor: "#9ca3af #f3f4f6" } : undefined}
      >
        {filteredOptions.length === 0 ? (
          <div className="px-3 py-2 text-sm text-gray-500">
            {searchTerm ? `${noOptionsMessage}` : noOptionsMessage}
          </div>
        ) : (
          filteredOptions.map((option, index) => (
            <button
              key={option.value || index}
              onClick={() => handleOptionSelect(option)}
              className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700 focus:bg-blue-50 focus:text-blue-700 focus:outline-none transition-colors duration-150"
              type="button"
            >
              {option.label}
            </button>
          ))
        )}
      </div>
    </div>
  );

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full md:w-[180px] ${className || ""}`}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-left text-sm shadow-sm hover:border-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer flex items-center justify-between"
        type="button"
      >
        <span className={`${!disableTruncation ? "truncate" : ""} text-gray-700`}>{getDisplayValue()}</span>
        <ChevronDown
          className={`ml-2 h-4 w-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && !floating && panel}
      {isOpen && floating && pos &&
        createPortal(
          <div
            ref={listRef}
            style={{ position: "fixed", left: pos.left, width: pos.width, top: pos.top, bottom: pos.bottom, zIndex: 10000 }}
          >
            {panel}
          </div>,
          document.body
        )}
    </div>
  );
};

export default SearchableDropdown;

